import { Injectable, OnDestroy } from '@angular/core';
import deepEqual from 'deep-equal';
import { BehaviorSubject, Observable, Subject, forkJoin, of, zip } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, switchMap, tap } from 'rxjs/operators';
import * as FileUtils from '../../../utils/file/file-utils';
import * as UrlUtils from '../../../utils/url/url-utils';
import { isOldSingleFileUpload, isOldWorkflowStarter } from '../../../utils/utils';
import { StepDeepCloneUtil } from '../../../utils/workflow/step-deep-cloner-utils';
import {
    getChangedSteps,
    getLatestExecutionModules,
    getRunSteps,
    getSelectedModules,
    getStepStatus,
    setModuleInputsFromRun,
    updateStepsDisabledState,
} from '../../../utils/workflow/workflow-runner-utils';
import { StepState } from '../enums/step-state.enum';
import { WorkflowRunInitType } from '../enums/workflow-run-init.enum';
import { ArchivedWorkflowRun } from '../models/archived-workflow-run.model';
import { FileModel } from '../models/file-model.model';
import { ListResult } from '../models/list-result';
import { ModuleRunStatus } from '../models/module-run-status.model';
import { ModuleShareRead, ModuleV1Read } from '../models/module-v1-read.model';
import { RunStep } from '../models/run-step.model';
import { WorkflowV1Read } from '../models/workflow-v1-read.model';
import { Workflow } from '../models/workflow.model';
import { DomainFactoryService } from '../services/domain-factory.service';
import { FileService } from '../services/file.service';
import { UserService } from '../services/user.service';
import { WorkflowRunApiService } from '../services/workflow-run-api.service';
import { WorkflowStateService } from '../services/workflow-state.service';
import { WorkflowV1Service } from '../services/workflow-v1.service';
import { ArivisService } from './arivis.service';
import { WorkflowRunService } from './workflow-run.service';

/** @README Detailed documentation is available in the WorkflowRunner.md */
@Injectable()
export class WorkflowRunnerStepBuilderService implements OnDestroy {
    get changedStep$(): Observable<Readonly<RunStep[]>> {
        return this.stepChanges$$.asObservable().pipe(
            pairwise(),
            map(pairs => {
                const [oldSteps, newSteps] = pairs;
                const validatedSteps = this.validateSteps(newSteps);
                const stepsWithUpdatedDisabledState = updateStepsDisabledState(
                    validatedSteps,
                    this.workflowRunService.runState,
                    this.workflowRunService.runMode,
                );
                return getChangedSteps(oldSteps, stepsWithUpdatedDisabledState);
            }),
            filter(steps => steps.length !== 0),
        );
    }

    get initialStep$(): Observable<Readonly<Readonly<RunStep>[]>> {
        return this.initialSteps$$.asObservable().pipe(
            map((steps: RunStep[]) => {
                const validatedSteps = this.validateSteps(steps);
                return updateStepsDisabledState(validatedSteps, this.workflowRunService.runState, this.workflowRunService.runMode);
            }),
        );
    }

    get stepState$(): Observable<StepState[]> {
        return this.stepChanges$$.asObservable().pipe(
            map(steps => {
                const validatedSteps = this.validateSteps(steps);
                const stepsWithUpdatedDisabledState = updateStepsDisabledState(
                    validatedSteps,
                    this.workflowRunService.runState,
                    this.workflowRunService.runMode,
                );
                return getStepStatus(stepsWithUpdatedDisabledState);
            }),
            distinctUntilChanged((oldStepState: StepState[], newStepState: StepState[]) => {
                return deepEqual(oldStepState, newStepState);
            }),
        );
    }

    get steps(): RunStep[] {
        return this.stepChanges$$.value;
    }

    get selectedRunStepIndex(): number {
        const stepIndex = this.stepChanges$$.value.findIndex(runStep => runStep.isSelectedInRunner);
        return stepIndex !== -1 ? stepIndex : 0;
    }

    private selectedModules: ModuleV1Read[] = [];
    private stepChanges$$: BehaviorSubject<RunStep[]> = new BehaviorSubject([]);
    private initialSteps$$: Subject<RunStep[]> = new Subject();

    constructor(
        private workflowV1Service: WorkflowV1Service,
        private workflowRunApiService: WorkflowRunApiService,
        private userService: UserService,
        private fileService: FileService,
        private workflowRunService: WorkflowRunService,
        private domainFactory: DomainFactoryService,
        private workflowStateService: WorkflowStateService,
        private arivisService: ArivisService,
    ) {}

    // we can use the onDestroy lifecycle as we provide the service only per component
    ngOnDestroy(): void {
        this.initialSteps$$.unsubscribe();
        this.stepChanges$$.unsubscribe();
    }

    triggerStepUpdate(): void {
        this.stepChanges$$.next(StepDeepCloneUtil.getDeepClonedSteps(this.stepChanges$$.value));
    }

    hasStepValidParameters(runStep: RunStep): boolean {
        let valid: boolean;

        if (runStep.isSingleFileUploadModule) {
            const srcUrl = runStep.input.src_url;
            const isNonEmptyString = typeof srcUrl === 'string' && srcUrl.length > 0;
            const isNonEmptyArray = srcUrl !== undefined && Array.isArray(srcUrl) && srcUrl.length > 0;
            valid = isNonEmptyString || isNonEmptyArray;
        } else if (this.domainFactory.isWorkflowDefineInput(runStep.module.id) || this.domainFactory.isModuleDefineInput(runStep.module.id)) {
            if (runStep.input.file != null && runStep.input.file !== '' && !Array.isArray(runStep.input.file)) {
                // Case: runStep.input.file is ONE string
                const fileUrl = runStep.input.file;
                valid = typeof fileUrl === 'string' && fileUrl.length > 0;
            } else if (runStep.input.file != null && Array.isArray(runStep.input.file)) {
                // Case: runStep.input.file is an array of strings (for batch run)
                valid = runStep.input.file.length > 0;
            } else if (runStep.input.file_list != null) {
                // Case: Single run with list of files.
                valid = runStep.input.file_list.length > 0;
            } else {
                valid = false;
            }
        } else {
            valid = runStep.isModuleParameterFormValid || false;
        }

        return valid;
    }

    validateSteps(steps: RunStep[]): RunStep[] {
        return steps.map((step: RunStep) => {
            const hasValidParameters = this.hasStepValidParameters(step);

            if (step.state === 'invalid-parameters' && hasValidParameters) {
                step.state = undefined;
            } else if (!hasValidParameters) {
                step.state = 'invalid-parameters';
            }

            return step;
        });
    }

    buildStepsByGivenWorkflowId$(workflowId: string): Observable<Workflow | null> {
        this.workflowRunService.runInitType = WorkflowRunInitType.ById;
        // for created workflow copy id will be different and get request to the backend will get fired
        if (this.workflowStateService.workflow != null && this.workflowStateService.workflow.id === workflowId) {
            const workflow = this.workflowStateService.workflow.getReadModelV1();
            this.workflowRunService.workflow = workflow;
            this.selectedModules = getSelectedModules(workflow);
            const runSteps = getRunSteps(workflow, this.selectedModules);
            this.initialSteps$$.next(runSteps);
            this.stepChanges$$.next(runSteps);
            return of(this.workflowStateService.workflow);
        }
        return this.workflowV1Service.getById(workflowId).pipe(
            map(workflowRunModelV1 => {
                this.workflowRunService.workflow = workflowRunModelV1;
                this.selectedModules = getSelectedModules(workflowRunModelV1);
                const runSteps = getRunSteps(workflowRunModelV1, this.selectedModules);
                this.initialSteps$$.next(runSteps);
                this.stepChanges$$.next(runSteps);
                this.workflowStateService.storeWorkflow(workflowRunModelV1);
                return this.workflowStateService.workflow;
            }),
        );
    }

    buildStepsForActiveWorkflow$(workflowRunId: string): Observable<[WorkflowV1Read, ArchivedWorkflowRun]> {
        this.workflowRunService.runInitType = WorkflowRunInitType.ByActiveWorkflow;
        this.workflowRunService.workflowRunId = workflowRunId;

        return this.workflowRunApiService.getArchivedWorkflowRun(workflowRunId).pipe(
            map(result => result.data),
            switchMap(archivedWorkflowRun =>
                zip(
                    this.workflowV1Service.createWorkflowFromArchivedWorkflowRun(archivedWorkflowRun, archivedWorkflowRun.workflowTitle),
                    of(archivedWorkflowRun),
                ),
            ),
            tap(([workflow, archivedWorkflowRun]: [WorkflowV1Read, ArchivedWorkflowRun]) => {
                this.workflowStateService.storeWorkflow(workflow);
                this.createNewStepsFromWorkflow(workflow, archivedWorkflowRun);
                this.workflowRunService.steps = this.steps;
                this.workflowRunService.pollWorkflowStatus(archivedWorkflowRun.workflowRunId);
            }),
        );
    }

    buildStepsForWorkflowReRun$(workflowRunId: string): Observable<[[WorkflowV1Read, ArchivedWorkflowRun], ListResult<FileModel>]> {
        this.workflowRunService.runInitType = WorkflowRunInitType.ReRun;
        this.workflowRunService.workflowRunId = workflowRunId;

        const workflowRunQueueServiceObservable$ = this.workflowRunApiService.getArchivedWorkflowRun(workflowRunId).pipe(
            map(result => result.data),
            switchMap((workflowRun: ArchivedWorkflowRun) => zip(this.workflowV1Service.createWorkflowFromArchivedWorkflowRun(workflowRun), of(workflowRun))),
        );

        const filesObservable$ = this.fileService.getFilesByWorkflowRunId$(workflowRunId);

        return forkJoin([workflowRunQueueServiceObservable$, filesObservable$]).pipe(
            tap((forkJoinedArr: [[WorkflowV1Read, ArchivedWorkflowRun], ListResult<FileModel>]) => {
                const [[wf, workflowRunArchive], fileResult] = forkJoinedArr;
                this.workflowStateService.storeWorkflow(wf);
                this.createNewStepsFromWorkflow(wf, workflowRunArchive, fileResult);
            }),
        );
    }

    updateSelectedStep(index: number): void {
        const runSteps = StepDeepCloneUtil.getDeepClonedSteps(this.stepChanges$$.value);

        for (let i = 0; i < runSteps.length; i++) {
            runSteps[i].isSelectedInRunner = i === index;
        }

        this.stepChanges$$.next(runSteps);
    }

    updateModuleDefineInputUrl(url: string[], step: RunStep, defineInputConnectedType?: 'file' | 'file_list'): void {
        const runSteps = StepDeepCloneUtil.getDeepClonedSteps(this.stepChanges$$.value);

        let moduleDefineInputStep = runSteps.find((s: RunStep) => s.flowId === step.flowId);

        if (moduleDefineInputStep != null) {
            moduleDefineInputStep = this.addFileUrlToSystemModule(url, moduleDefineInputStep, defineInputConnectedType);

            this.stepChanges$$.next(runSteps);
        }
    }

    updateWorkflowStarterUrl(url: string | string[], defineInputConnectedType?: 'file' | 'file_list'): void {
        const runSteps = StepDeepCloneUtil.getDeepClonedSteps(this.stepChanges$$.value);

        this.addFileUrlToSystemModule(url, runSteps[0], defineInputConnectedType);
        this.stepChanges$$.next(runSteps);
    }

    getFilesFromSystemModule(workflowStarterStep: RunStep): Array<string> {
        let filesUrls: Array<string> = [];
        if (this.domainFactory.isWorkflowDefineInput(workflowStarterStep.module.id) || this.domainFactory.isModuleDefineInput(workflowStarterStep.module.id)) {
            if (workflowStarterStep.input.file != null && workflowStarterStep.input.file !== '') {
                filesUrls.push(workflowStarterStep.input.file);
            } else if (workflowStarterStep.input.file_list != null && workflowStarterStep.input.file_list.length > 0) {
                filesUrls = workflowStarterStep.input.file_list;
            }
        } else if (workflowStarterStep.input != null && workflowStarterStep.input.src_url != null) {
            // Backward compatibility to old implementation
            if (Array.isArray(workflowStarterStep.input.src_url)) {
                filesUrls = workflowStarterStep.input.src_url;
            } else if (workflowStarterStep.input.src_url !== '') {
                filesUrls.push(workflowStarterStep.input.src_url);
            }
        }

        return filesUrls;
    }

    /**
     *
     * @param activeConnectionType - can be 'file' or 'file_list' according to what is connected to the system module.
     *                                 undefined if no connection is available
     */
    addFileUrlToSystemModule(url: string | string[], workflowStarterStep: RunStep, activeConnectionType?: 'file' | 'file_list'): RunStep {
        // New workflow define Input implementation
        if (this.domainFactory.isWorkflowDefineInput(workflowStarterStep.module.id) || this.domainFactory.isModuleDefineInput(workflowStarterStep.module.id)) {
            // If no parameter is connected to ModuleDefineInput/WorkflowDefineInput
            if (activeConnectionType != null) {
                if (activeConnectionType === 'file') {
                    this.setUrlsToFileParameter(workflowStarterStep, url);
                } else {
                    this.setUrlsToFileListParameter(workflowStarterStep, url);
                }
            } else if (Array.isArray(url)) {
                if (url.length > 1) {
                    this.setUrlsToFileListParameter(workflowStarterStep, url);
                } else {
                    this.setUrlsToFileParameter(workflowStarterStep, url);
                }
            } else {
                this.setUrlsToFileParameter(workflowStarterStep, url);
            }
        } else {
            // Set URL on step
            // fallback to old implementation (with src_url)
            if (Array.isArray(url)) {
                workflowStarterStep.input.src_url = url;
            } else {
                if (!workflowStarterStep.input.src_url) {
                    workflowStarterStep.input.src_url = [];
                }
                workflowStarterStep.input.src_url.push(url);
            }
        }

        return workflowStarterStep;
    }

    removeWorkflowStarterUrl(url: string): void {
        const runSteps = StepDeepCloneUtil.getDeepClonedSteps(this.stepChanges$$.value);

        const workflowStarterStep = runSteps[0];

        if (!workflowStarterStep.input.src_url || workflowStarterStep.input.src_url.length === 0) {
            return;
        }

        const newSrcUrl = workflowStarterStep.input.src_url.filter((srcUrl: string) => srcUrl !== url);

        if (workflowStarterStep.input.src_url !== newSrcUrl) {
            workflowStarterStep.input.src_url = newSrcUrl;
            this.stepChanges$$.next(runSteps);
        }
    }

    updateModuleParameters(runStep: RunStep, input: Object, valid: boolean): void {
        const runSteps = StepDeepCloneUtil.getDeepClonedSteps(this.stepChanges$$.value);

        const index = runSteps.findIndex(step => step.flowId === runStep.flowId);
        const newRunStep = runSteps[index];

        if (!deepEqual(newRunStep.input, input)) {
            newRunStep.isModuleParameterFormValid = valid;
            newRunStep.input = input;
            this.stepChanges$$.next(runSteps);
        }
    }

    updateModuleRunStatusAndResults(modules: ModuleRunStatus[], resultFiles?: FileModel[]): void {
        const runSteps = StepDeepCloneUtil.getDeepClonedSteps(this.stepChanges$$.value);
        modules = getLatestExecutionModules(modules);
        for (const module of modules) {
            const runStep: RunStep = runSteps[module.moduleId];
            runStep.state = module.state;

            if (module.outputs) {
                runStep.results = Object.entries({ ...module.outputs }).map((entry: [string, FileModel[] | FileModel | undefined]) => {
                    const [key, value] = entry;
                    let valueToReturn: FileModel[] | FileModel | undefined = value;
                    let matchBy: string = `module_${module.moduleId}_${module.stepId}`;
                    if (module.iteration != null) {
                        matchBy += `_${module.iteration}`;
                    }

                    if (Array.isArray(value)) {
                        valueToReturn = [];
                        const values: Array<FileModel> = value;
                        for (const val of values) {
                            const foundArivisFileInArray: FileModel | undefined = this.findFileForArivis(val, matchBy, resultFiles);
                            if (foundArivisFileInArray) {
                                valueToReturn.push(foundArivisFileInArray);
                            } else {
                                valueToReturn.push(val as FileModel);
                            }
                        }
                    } else {
                        const foundArivisFile: FileModel | undefined = this.findFileForArivis(value, matchBy, resultFiles);
                        if (foundArivisFile) {
                            valueToReturn = foundArivisFile;
                        }
                    }

                    return { key, value: valueToReturn };
                });
            }
        }

        this.stepChanges$$.next(runSteps);
    }

    setStepAsCompletedInStepByStep(runStep: RunStep): void {
        const runSteps = StepDeepCloneUtil.getDeepClonedSteps(this.stepChanges$$.value);

        const index = runSteps.findIndex(step => step.flowId === runStep.flowId);
        if (index > -1) {
            runSteps[index].isCompletedInStepByStep = true;
        }
        this.stepChanges$$.next(runSteps);
    }

    isSystemModule(moduleId: string, moduleTitle: string): boolean {
        return (
            this.domainFactory.isWorkflowDefineInput(moduleId) ||
            this.domainFactory.isModuleDefineInput(moduleId) ||
            isOldWorkflowStarter(moduleTitle, 0) ||
            isOldSingleFileUpload(moduleTitle, 1)
        );
    }

    isModuleSharedWithMe(module: ModuleV1Read): boolean {
        let isShared = false;
        if (module.shares && module.shares.length > 0 && this.userService.myInfo != null) {
            isShared = module.shares.filter((user: ModuleShareRead) => user.userId === this.userService.myInfo!.sub).length > 0;
        }

        return isShared;
    }

    private setUrlsToFileListParameter(workflowStarterStep: RunStep, url: string | string[]): void {
        workflowStarterStep.input.file = undefined;
        workflowStarterStep.input.file_list = url;
    }

    private setUrlsToFileParameter(workflowStarterStep: RunStep, url: string | string[]): void {
        workflowStarterStep.input.file = url;
        workflowStarterStep.input.file_list = [];
    }

    private createNewStepsFromWorkflow(workflow: WorkflowV1Read, workflowRunArchive: ArchivedWorkflowRun, fileResult?: ListResult<FileModel>): void {
        this.workflowRunService.workflow = workflow;
        this.selectedModules = getSelectedModules(workflow);
        const runSteps = getRunSteps(workflow, this.selectedModules, fileResult);
        const newRunSteps = setModuleInputsFromRun(workflowRunArchive, runSteps);

        this.stepChanges$$.next(newRunSteps);
        this.initialSteps$$.next(newRunSteps);
    }

    private findFileForArivis(val: FileModel | undefined, matchBy: string, resultFiles?: FileModel[]): FileModel | undefined {
        if (
            !resultFiles ||
            !matchBy ||
            val == null ||
            typeof val !== 'string' ||
            !UrlUtils.isUrl(val) ||
            !this.arivisService.isSupportedByArivis(FileUtils.getFileExtension(val) || '')
        ) {
            return undefined;
        }

        const fileName = decodeURIComponent(FileUtils.getFileNameWithExtension(val));
        return resultFiles.find(file => file.token && decodeURIComponent(file.token).indexOf(matchBy + '/' + fileName) > -1);
    }
}
